import { LuOrbit } from "react-icons/lu";
import {
  SiEthereum,
  SiFacebook,
  SiInstagram,
  SiSoundcloud,
  SiSpotify,
  SiTiktok,
  SiTwitch,
  SiX,
  SiYoutube,
} from "react-icons/si";

import { useEditorContext } from "@/components/app/editor-v3/editor-context";
import { Checkbox } from "@/components/ui/checkbox";
import { OPENFUND_APP_NAME } from "@/utils/constants";

export const EditorEmbedActionDetails = () => {
  const { disabledURLs, detectedURLs, setEditorContext } = useEditorContext();

  return (
    <div className="flex min-w-0 flex-col gap-4 text-sm/6">
      {!detectedURLs.length && (
        <p className="text-muted">
          You didn't add any URLs to embed. To add an embed, simply paste a URL
          in the editor.
        </p>
      )}
      {!!detectedURLs.length && (
        <>
          <p className="text-muted">
            Select which embeds/previews you would like to display in your post:
          </p>
          <ul
            className="grid max-w-full min-w-0 gap-2"
            data-editor-v3-embed-url-list
          >
            {detectedURLs.map(({ url }, i) => (
              <li key={url} className="min-w-0" data-editor-v3-embed-url-row>
                <div className="flex max-w-full min-w-0 items-center gap-2 overflow-hidden">
                  <Checkbox
                    className="shrink-0"
                    id={`embed-checkbox-${i}`}
                    defaultChecked={!disabledURLs.includes(url)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setEditorContext((prev) => ({
                          disabledURLs: prev.disabledURLs!.filter(
                            (u) => u !== url,
                          ),
                        }));
                      } else {
                        setEditorContext((prev) => ({
                          disabledURLs: [...prev.disabledURLs!, url],
                        }));
                      }
                    }}
                  />
                  <label
                    className="min-w-0 flex-1 truncate"
                    htmlFor={`embed-checkbox-${i}`}
                    title={url}
                  >
                    {url}
                  </label>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      <details>
        <summary>
          <p
            className="
              inline-block cursor-pointer pl-2 font-semibold
              hover:text-muted-foreground
            "
          >
            Supported platforms <span className="text-muted">(and media)</span>{" "}
            types
          </p>
        </summary>
        <ul
          className="
            mt-4 grid list-none gap-4
            md:grid-cols-2
          "
        >
          <li
            className="
              flex items-center gap-2 rounded-full border p-4 py-2 text-muted
            "
          >
            <span
              className="
                flex items-center gap-2 font-semibold text-muted-foreground
              "
            >
              <SiX className="text-muted" /> X.com
            </span>{" "}
            <span className="text-xs text-muted">Posts, Profiles</span>
          </li>
          <li
            className="
              flex items-center gap-2 rounded-full border p-4 py-2 text-muted
            "
          >
            <span
              className="
                flex items-center gap-2 font-semibold text-muted-foreground
              "
            >
              <SiYoutube className="text-muted" /> YouTube
            </span>{" "}
            <span className="text-xs text-muted">Videos</span>
          </li>
          <li
            className="
              flex items-center gap-2 rounded-full border p-4 py-2 text-muted
            "
          >
            <span
              className="
                flex items-center gap-2 font-semibold text-muted-foreground
              "
            >
              <SiSpotify className="text-muted" /> Spotify
            </span>{" "}
            <span className="text-xs text-muted">
              Track, Playlist, Album, Artist, Episode
            </span>
          </li>
          <li
            className="
              flex items-center gap-2 rounded-full border p-4 py-2 text-muted
            "
          >
            <span
              className="
                flex items-center gap-2 font-semibold text-muted-foreground
              "
            >
              <SiInstagram className="text-muted" /> Instagram
            </span>{" "}
            <span className="text-xs text-muted">Posts</span>
          </li>
          <li
            className="
              flex items-center gap-2 rounded-full border p-4 py-2 text-muted
            "
          >
            <span
              className="
                flex items-center gap-2 font-semibold text-muted-foreground
              "
            >
              <SiFacebook className="text-muted" /> Facebook
            </span>{" "}
            <span className="text-xs text-muted">Videos</span>
          </li>
          <li
            className="
              flex items-center gap-2 rounded-full border p-4 py-2 text-muted
            "
          >
            <span
              className="
                flex items-center gap-2 font-semibold text-muted-foreground
              "
            >
              <SiTiktok className="text-muted" /> TikTok
            </span>{" "}
            <span className="text-xs text-muted">Videos</span>
          </li>
          <li
            className="
              flex items-center gap-2 rounded-full border p-4 py-2 text-muted
            "
          >
            <span
              className="
                flex items-center gap-2 font-semibold text-muted-foreground
              "
            >
              <SiTwitch className="text-muted" /> Twitch
            </span>{" "}
            <span className="text-xs text-muted">Videos, Channels</span>
          </li>
          <li
            className="
              flex items-center gap-2 rounded-full border p-4 py-2 text-muted
            "
          >
            <span
              className="
                flex items-center gap-2 font-semibold text-muted-foreground
              "
            >
              <SiEthereum className="text-muted" /> DexScreener
            </span>{" "}
            <span className="text-xs text-muted">Charts</span>
          </li>
          <li
            className="
              flex items-center gap-2 rounded-full border p-4 py-2 text-muted
            "
          >
            <span
              className="
                flex items-center gap-2 font-semibold text-muted-foreground
              "
            >
              <SiSoundcloud className="text-muted" /> SoundCloud
            </span>{" "}
            <span className="text-xs text-muted">
              Tracks, Artists, Playlists
            </span>
          </li>
          <li
            className="
              flex items-center gap-2 rounded-full border p-4 py-2 text-muted
            "
          >
            <span
              className="
                flex items-center gap-2 font-semibold text-muted-foreground
              "
            >
              <LuOrbit className="text-muted" /> {OPENFUND_APP_NAME}
            </span>{" "}
            <span className="text-xs text-muted">Charts</span>
          </li>
        </ul>
      </details>
    </div>
  );
};
